import React, { useState, useMemo, useRef } from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import Table from '@amzn/awsui-components-console/table';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import { TaskTypeListItem } from 'src/stores/taskTypeListStore/taskTypeListStore.interface';
import { useAuthContext } from 'src/providers/AuthContext';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import { createTaskDefinitionFromType } from '../createTaskDefinitionUtils';

export interface AddTaskModalProps {
	visible: boolean;
	onDismiss: () => void;
	onAdd: (taskType: TaskTypeListItem, newTaskId: string) => void;
}

const TASK_TYPE_COLUMN_DEFINITIONS = [
	{
		id: 'taskName',
		header: 'Task Name',
		cell: (item: TaskTypeListItem) => item.taskName,
		sortingField: 'taskName',
	},
	{
		id: 'applicationName',
		header: 'Application',
		cell: (item: TaskTypeListItem) => item.applicationName || 'SNOWPlatform',
		sortingField: 'applicationName',
	},
	{
		id: 'description',
		header: 'Description',
		cell: (item: TaskTypeListItem) => item.description || '-',
	},
];

export default function AddTaskModal({
	visible,
	onDismiss,
	onAdd,
}: AddTaskModalProps) {
	const [filteringText, setFilteringText] = useState('');
	const [selectedItems, setSelectedItems] = useState<TaskTypeListItem[]>([]);
	const [isAddingSubWorkflow, setIsAddingSubWorkflow] = useState(false);
	const addRequestIdRef = useRef(0);

	const taskTypeList = useStore((state: Store) => state.taskTypeList);
	const subWorkflowList = useStore((state: Store) => state.subWorkflowList);
	const isTaskTypeListLoading = useStore(
		(state: Store) => state.isTaskTypeListLoading
	);
	const addTaskDefinition = useStore((state: Store) => state.addTaskDefinition);
	const fetchChildWorkflowDefinition = useStore(
		(state: Store) => state.fetchChildWorkflowDefinition
	);
	const entityType = useStore(
		(state: Store) => state.workflowCreationMetaData.entityType
	);
	const authContext = useAuthContext();
	const userName = authContext.userName as string;

	const filteredList = useMemo(() => {
		const addableTaskTypes = [...taskTypeList, ...subWorkflowList];

		if (!filteringText) return addableTaskTypes;
		const lower = filteringText.toLowerCase();

		return addableTaskTypes.filter(
			(t) =>
				t.taskName.toLowerCase().includes(lower) ||
				(t.description?.toLowerCase().includes(lower) ?? false) ||
				(t.applicationName || 'SNOWPlatform').toLowerCase().includes(lower)
		);
	}, [taskTypeList, subWorkflowList, filteringText]);

	async function handleAdd() {
		if (selectedItems.length === 0) return;
		const taskType = selectedItems[0];
		const taskDefinition = createTaskDefinitionFromType(
			taskType,
			userName,
			entityType
		);

		if (taskType.isSubWorkflow && taskType.childWorkflowId) {
			const requestId = ++addRequestIdRef.current;

			setIsAddingSubWorkflow(true);

			const childWorkflowDefinition = await fetchChildWorkflowDefinition(
				taskType.childWorkflowId
			);

			if (requestId !== addRequestIdRef.current) return;

			setIsAddingSubWorkflow(false);

			if (!childWorkflowDefinition) return;
		}

		addTaskDefinition(taskDefinition);
		onAdd(taskType, taskDefinition.taskId);
		handleDismiss();
	}

	function handleDismiss() {
		addRequestIdRef.current += 1;
		setIsAddingSubWorkflow(false);
		setFilteringText('');
		setSelectedItems([]);
		onDismiss();
	}

	return (
		<Modal
			visible={visible}
			onDismiss={handleDismiss}
			header={`Add Task (${filteredList.length})`}
			size="large"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={handleDismiss}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleAdd}
							loading={isAddingSubWorkflow}
							disabled={selectedItems.length === 0}
						>
							Add Task
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			<SpaceBetween size="m">
				<TextFilter
					filteringText={filteringText}
					filteringPlaceholder="Search task types"
					filteringAriaLabel="Search task types"
					onChange={({ detail }) => setFilteringText(detail.filteringText)}
				/>
				<Table
					columnDefinitions={TASK_TYPE_COLUMN_DEFINITIONS}
					items={filteredList}
					loading={isTaskTypeListLoading}
					loadingText="Loading supported tasks..."
					selectionType="single"
					selectedItems={selectedItems}
					onSelectionChange={({ detail }) =>
						setSelectedItems(detail.selectedItems)
					}
					onRowClick={({ detail }) => {
						setSelectedItems([detail.item]);
					}}
					stripedRows={getDefaultStripedRows()}
					trackBy="taskId"
					empty={
						<Box textAlign="center" color="inherit">
							<Box padding={{ bottom: 's' }} variant="p" color="inherit">
								No task types found.
							</Box>
						</Box>
					}
					variant="embedded"
				/>
			</SpaceBetween>
		</Modal>
	);
}
